import type { ActivityCockpitSnapshot } from '@internal/spassu-code-contract';
import {
  allCockpitNodes,
  type CockpitViewModel,
} from '../model/cockpitViewModel';
import {
  cockpitSelectionReducer,
  EMPTY_COCKPIT_SELECTION,
  relatedNodeIds,
} from '../selection/cockpitSelection';
import {
  mergeArtifactPages,
  mergePrimaryMetrics,
  mergeTimelinePages,
} from './cockpitControllerMerge';
import type {
  CockpitControllerAction,
  CockpitControllerState,
} from './cockpitControllerTypes';

export function createCockpitControllerState(
  activityId: string,
  timelineQuery = {},
): CockpitControllerState {
  return {
    activityId,
    phase: 'loading',
    snapshot: null,
    model: null,
    refreshing: false,
    stale: false,
    error: null,
    timeline: {
      status: 'idle',
      query: timelineQuery,
      page: null,
      error: null,
    },
    artifactContent: {},
    selection: EMPTY_COCKPIT_SELECTION,
  };
}

export function cockpitControllerReducer(
  state: CockpitControllerState,
  action: CockpitControllerAction,
): CockpitControllerState {
  if (isSnapshotAction(action)) return reduceSnapshot(state, action);
  if (isTimelineAction(action)) return reduceTimeline(state, action);
  if (isArtifactAction(action)) return reduceArtifact(state, action);
  switch (action.type) {
    case 'reset':
      return createCockpitControllerState(
        action.activityId,
        action.timelineQuery,
      );
    case 'select':
      return {
        ...state,
        selection: cockpitSelectionReducer(state.selection, {
          type: 'select',
          selection: action.selection,
          relations: state.model?.relations ?? [],
        }),
      };
    case 'clearSelection':
      return {
        ...state,
        selection: cockpitSelectionReducer(state.selection, { type: 'clear' }),
      };
    default:
      return unchangedState(state, action);
  }
}

// `never` makes new typed actions require a routing decision; keep the existing
// runtime fallback if an untyped caller supplies an unknown action.
function unchangedState(
  state: CockpitControllerState,
  _action: never,
): CockpitControllerState {
  return state;
}

type SnapshotAction = Extract<
  CockpitControllerAction,
  { type: 'snapshotStarted' | 'snapshotSucceeded' | 'snapshotFailed' }
>;
type TimelineAction = Extract<
  CockpitControllerAction,
  {
    type:
      | 'timelineQueryChanged'
      | 'timelineStarted'
      | 'timelineSucceeded'
      | 'timelineFailed';
  }
>;
type ArtifactAction = Extract<
  CockpitControllerAction,
  { type: 'artifactStarted' | 'artifactSucceeded' | 'artifactFailed' }
>;

function isSnapshotAction(
  action: CockpitControllerAction,
): action is SnapshotAction {
  return (
    action.type === 'snapshotStarted' ||
    action.type === 'snapshotSucceeded' ||
    action.type === 'snapshotFailed'
  );
}

function isTimelineAction(
  action: CockpitControllerAction,
): action is TimelineAction {
  return (
    action.type === 'timelineQueryChanged' ||
    action.type === 'timelineStarted' ||
    action.type === 'timelineSucceeded' ||
    action.type === 'timelineFailed'
  );
}

function isArtifactAction(
  action: CockpitControllerAction,
): action is ArtifactAction {
  return (
    action.type === 'artifactStarted' ||
    action.type === 'artifactSucceeded' ||
    action.type === 'artifactFailed'
  );
}

function reduceSnapshot(
  state: CockpitControllerState,
  action: CockpitControllerAction,
): CockpitControllerState {
  if (action.type === 'snapshotStarted') {
    return snapshotStarted(state, action.activityId);
  }
  if (action.type === 'snapshotSucceeded') {
    return snapshotSucceeded(state, action.snapshot, action.model);
  }
  if (action.type !== 'snapshotFailed') return state;
  return {
    ...state,
    phase: state.model ? 'ready' : 'error',
    refreshing: false,
    stale: action.stale,
    error: action.error,
  };
}

function reduceTimeline(
  state: CockpitControllerState,
  action: CockpitControllerAction,
): CockpitControllerState {
  if (action.type === 'timelineQueryChanged') {
    return updateTimeline(state, { query: action.query });
  }
  if (action.type === 'timelineStarted') {
    return updateTimeline(state, {
      status: 'loading',
      page: state.timeline.page,
      error: null,
    });
  }
  if (action.type === 'timelineSucceeded') {
    return updateTimeline(state, {
      status: 'ready',
      page: mergeTimelinePages(state.timeline.page, action.page, action.reset),
      query: {
        ...state.timeline.query,
        ...action.page.filters,
        page: action.page.page,
        limit: action.page.pageSize,
      },
      error: null,
    });
  }
  if (action.type !== 'timelineFailed') return state;
  return updateTimeline(state, { status: 'error', error: action.error });
}

function reduceArtifact(
  state: CockpitControllerState,
  action: CockpitControllerAction,
): CockpitControllerState {
  if (action.type === 'artifactStarted') {
    return updateArtifact(state, action.artifactId, current => ({
      status: 'loading',
      page: current?.page ?? null,
      error: null,
    }));
  }
  if (action.type === 'artifactSucceeded') {
    return updateArtifact(state, action.artifactId, current => ({
      status: 'ready',
      page: mergeArtifactPages(current?.page ?? null, action.page),
      error: null,
    }));
  }
  if (action.type !== 'artifactFailed') return state;
  return updateArtifact(state, action.artifactId, current => ({
    status: action.forbidden ? 'forbidden' : 'error',
    page: action.forbidden ? null : current?.page ?? null,
    error: action.error,
  }));
}

function updateTimeline(
  state: CockpitControllerState,
  timeline: Partial<CockpitControllerState['timeline']>,
): CockpitControllerState {
  return { ...state, timeline: { ...state.timeline, ...timeline } };
}

function snapshotStarted(
  state: CockpitControllerState,
  activityId: string,
): CockpitControllerState {
  if (state.activityId !== activityId) {
    return {
      ...createCockpitControllerState(activityId, state.timeline.query),
      refreshing: false,
    };
  }
  return {
    ...state,
    phase: state.model ? 'ready' : 'loading',
    refreshing: state.model !== null,
    error: null,
  };
}

function snapshotSucceeded(
  state: CockpitControllerState,
  snapshot: ActivityCockpitSnapshot,
  model: CockpitViewModel,
): CockpitControllerState {
  const mergedModel = {
    ...model,
    metrics: mergePrimaryMetrics(state.model?.metrics, model.metrics),
  };
  const inspectable = new Set(
    mergedModel.artifacts
      .filter(artifact => artifact.access === 'inspectable')
      .map(artifact => artifact.id),
  );
  return {
    ...state,
    activityId: snapshot.activityId,
    phase: 'ready',
    snapshot,
    model: mergedModel,
    refreshing: false,
    stale: false,
    error: null,
    artifactContent: Object.fromEntries(
      Object.entries(state.artifactContent).filter(([id]) =>
        inspectable.has(id),
      ),
    ),
    selection: reconcileSelection(
      state.selection,
      mergedModel,
      state.timeline.page,
    ),
  };
}

function reconcileSelection(
  selection: CockpitControllerState['selection'],
  model: CockpitViewModel,
  timeline: CockpitControllerState['timeline']['page'],
) {
  const timelineMarkerAvailable = timeline?.markers.some(
    marker => marker.id === selection.selected?.id,
  );
  if (
    !selection.selected ||
    (!timelineMarkerAvailable &&
      !allCockpitNodes(model).some(node => node.id === selection.selected?.id))
  ) {
    return EMPTY_COCKPIT_SELECTION;
  }
  return {
    selected: { ...selection.selected, revision: model.revision },
    relatedIds: relatedNodeIds(selection.selected.id, model.relations),
  };
}

function updateArtifact(
  state: CockpitControllerState,
  artifactId: string,
  update: (
    current: CockpitControllerState['artifactContent'][string] | undefined,
  ) => CockpitControllerState['artifactContent'][string],
): CockpitControllerState {
  return {
    ...state,
    artifactContent: {
      ...state.artifactContent,
      [artifactId]: update(state.artifactContent[artifactId]),
    },
  };
}
